import { useState, useEffect, useCallback } from 'react';

interface ScaleConfig {
  originalWidth: number;
  originalHeight: number;
  minScale?: number;
  maxScale?: number;
  padding?: number;
}

export const useDynamicScale = ({
  originalWidth,
  originalHeight,
  minScale = 0.5,
  maxScale = Infinity,
  padding = 40
}: ScaleConfig) => {
  const [scale, setScale] = useState(0.6667);
  const [containerDimensions, setContainerDimensions] = useState({
    width: originalWidth * 0.6667,
    height: originalHeight * 0.6667
  });
  const [containerDimensionsEm, setContainerDimensionsEm] = useState({
    width: '58.33em', // 933px / 16px
    height: '35.44em' // 567px / 16px
  });

  const calculateScale = useCallback(() => {
    // Get the root font size (typically 16px)
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    
    const viewportWidth = window.innerWidth - padding;
    const viewportHeight = window.innerHeight - padding;
    
    // Calculate scale factors for width and height
    const scaleX = viewportWidth / originalWidth;
    const scaleY = viewportHeight / originalHeight;
    
    // Use the smaller scale to ensure content fits in both dimensions
    let newScale = Math.min(scaleX, scaleY);
    
    // Clamp scale between min and max
    newScale = Math.max(minScale, Math.min(maxScale, newScale));
    
    // Round to 4 decimal places for cleaner CSS
    newScale = Math.round(newScale * 10000) / 10000;
    
    const scaledWidth = Math.round(originalWidth * newScale);
    const scaledHeight = Math.round(originalHeight * newScale);
    
    setScale(newScale);
    setContainerDimensions({
      width: scaledWidth,
      height: scaledHeight
    });
    setContainerDimensionsEm({
      width: `${(scaledWidth / rootFontSize).toFixed(2)}em`,
      height: `${(scaledHeight / rootFontSize).toFixed(2)}em`
    });
  }, [originalWidth, originalHeight, minScale, maxScale, padding]);

  useEffect(() => {
    calculateScale();
    
    const handleResize = () => {
      calculateScale();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateScale]);

  return { scale, containerDimensions, containerDimensionsEm };
};